import { useState, useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Chip,
  Stack
} from '@mui/material'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels)

// Longevity study data structured for Before/After comparison at different time points
// Based on corpus data from longevity_time_series.json and longevity_studies.json
const longevityData = {
  studies: [
    {
      id: 'major_global_bank_a',
      name: 'Major Global Bank A',
      shortName: 'Bank A Plaza',
      industry: 'Financial Services',
      equipmentType: 'Split System DX',
      measurements: [
        { period: 'Initial', years: 0, baseEff: 0.752, postEff: 0.886, records: 4 },
        { period: '2 Years', years: 2, baseEff: 0.731, postEff: 0.883, records: 4 },
        { period: '4 Years', years: 4, baseEff: 0.710, postEff: 0.880, records: 4 },
        { period: '6 Years', years: 6, baseEff: 0.689, postEff: 0.877, records: 4 }
      ]
    },
    {
      id: 'qsr_chain_a',
      name: 'QSR Chain A',
      shortName: 'QSR Chain A',
      industry: 'Quick Service Restaurant',
      equipmentType: 'RTU',
      measurements: [
        { period: 'Initial', years: 0, baseEff: 0.841, postEff: 0.934, records: 44 },
        { period: '2 Years', years: 2, baseEff: 0.806, postEff: 0.931, records: 44 }
      ]
    },
    {
      id: 'restaurant_chain_a',
      name: 'Restaurant Chain A',
      shortName: 'Rest. Chain A',
      industry: 'Casual Dining',
      equipmentType: 'RTU',
      measurements: [
        { period: 'Initial', years: 0, baseEff: 0.740, postEff: 0.974, records: 16 },
        { period: '2 Years', years: 2, baseEff: 0.712, postEff: 0.970, records: 16 }
      ]
    },
    {
      id: 'major_telecom_e',
      name: 'Major Telecom E',
      shortName: 'Telecom E',
      industry: 'Telecommunications',
      equipmentType: 'Package DX',
      measurements: [
        { period: 'Initial', years: 0, baseEff: 0.806, postEff: 0.991, records: 16 },
        { period: '1 Year', years: 1, baseEff: 0.783, postEff: 0.988, records: 16 }
      ]
    }
  ],
  timePeriods: ['Initial', '1 Year', '2 Years', '4 Years', '6 Years']
}

function LongevityPerformance() {
  // Filter states
  const [selectedPeriods, setSelectedPeriods] = useState(
    new Set(['Initial', '1 Year', '2 Years', '4 Years', '6 Years'])
  )
  const [selectedStudies, setSelectedStudies] = useState(
    new Set(longevityData.studies.map(s => s.id))
  )

  // Get unique time periods from actual data
  const availablePeriods = useMemo(() => {
    const periods = new Set()
    longevityData.studies.forEach(study => {
      study.measurements.forEach(m => periods.add(m.period))
    })
    return Array.from(periods).sort((a, b) => {
      const order = { 'Initial': 0, '1 Year': 1, '2 Years': 2, '4 Years': 4, '6 Years': 6 }
      return (order[a] || 0) - (order[b] || 0)
    })
  }, [])

  // Toggle functions
  const togglePeriod = (period) => {
    setSelectedPeriods(prev => {
      const newSet = new Set(prev)
      if (newSet.has(period)) {
        newSet.delete(period)
      } else {
        newSet.add(period)
      }
      return newSet
    })
  }

  const toggleStudy = (studyId) => {
    setSelectedStudies(prev => {
      const newSet = new Set(prev)
      if (newSet.has(studyId)) {
        newSet.delete(studyId)
      } else {
        newSet.add(studyId)
      }
      return newSet
    })
  }

  const toggleAllPeriods = () => {
    if (selectedPeriods.size === availablePeriods.length) {
      setSelectedPeriods(new Set())
    } else {
      setSelectedPeriods(new Set(availablePeriods))
    }
  }

  const toggleAllStudies = () => {
    if (selectedStudies.size === longevityData.studies.length) {
      setSelectedStudies(new Set())
    } else {
      setSelectedStudies(new Set(longevityData.studies.map(s => s.id)))
    }
  }

  // Prepare chart data
  const chartData = useMemo(() => {
    const labels = []
    const baseEffData = []
    const postEffData = []
    const metaData = []

    longevityData.studies
      .filter(study => selectedStudies.has(study.id))
      .forEach(study => {
        study.measurements
          .filter(m => selectedPeriods.has(m.period))
          .forEach(measurement => {
            const label = `${study.shortName}\n${measurement.period}`
            labels.push(label)
            baseEffData.push(measurement.baseEff)
            postEffData.push(measurement.postEff)
            metaData.push({
              studyName: study.name,
              period: measurement.period,
              years: measurement.years,
              records: measurement.records,
              equipmentType: study.equipmentType,
              industry: study.industry,
              improvement: ((measurement.postEff - measurement.baseEff) / measurement.baseEff * 100).toFixed(1)
            })
          })
      })

    return {
      labels,
      datasets: [
        {
          label: 'Avg. Base Eff',
          data: baseEffData,
          backgroundColor: '#2196F3',
          borderColor: '#1565C0',
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.7
        },
        {
          label: 'Avg. Post Unit Eff',
          data: postEffData,
          backgroundColor: '#FF9800',
          borderColor: '#E65100',
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.7
        }
      ],
      metaData
    }
  }, [selectedPeriods, selectedStudies])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'x',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12 }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        padding: 12,
        callbacks: {
          title: (context) => {
            const meta = chartData.metaData[context[0].dataIndex]
            return `${meta.studyName}`
          },
          label: (context) => {
            const value = context.raw
            return `${context.dataset.label}: ${(value * 100).toFixed(2)}%`
          },
          afterBody: (context) => {
            const meta = chartData.metaData[context[0].dataIndex]
            return [
              '',
              `Period: ${meta.period}`,
              `Improvement: +${meta.improvement}%`,
              `Equipment: ${meta.equipmentType}`,
              `Industry: ${meta.industry}`,
              `Number of Records: ${meta.records}`
            ]
          }
        }
      },
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'top',
        offset: 2,
        font: {
          size: 10,
          weight: 'bold'
        },
        color: (context) => {
          return context.dataset.backgroundColor === '#2196F3' ? '#1565C0' : '#E65100'
        },
        formatter: (value) => value.toFixed(4)
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          font: { size: 10 },
          autoSkip: false,
          callback: function(value, index) {
            const label = this.getLabelForValue(value)
            // Split multi-line labels — abbreviate names
            const parts = label.split('\n')
            if (parts[0]) {
              parts[0] = parts[0].replace('Bank A Plaza', 'Bank A').replace('QSR Chain A', 'QSR A').replace('Rest. Chain A', 'Rest. A').replace('Telecom E', 'Tel. E')
            }
            return parts
          }
        }
      },
      y: {
        min: 0,
        max: 1.1,
        title: {
          display: true,
          text: 'Efficiency (0-1 scale)',
          font: { size: 12 }
        },
        ticks: {
          callback: (value) => `${(value * 100).toFixed(0)}%`
        }
      }
    }
  }

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (chartData.metaData.length === 0) return null

    const totalRecords = chartData.metaData.reduce((sum, m) => sum + m.records, 0)
    const avgImprovement = chartData.metaData.reduce((sum, m) => sum + parseFloat(m.improvement), 0) / chartData.metaData.length
    const uniqueStudies = new Set(chartData.metaData.map(m => m.studyName)).size

    return {
      totalRecords,
      avgImprovement: avgImprovement.toFixed(1),
      uniqueStudies,
      measurements: chartData.metaData.length
    }
  }, [chartData])

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {/* Main Chart Area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Long Term Performance
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Study For / Years Since Treatment
        </Typography>

        {summaryStats && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              label={`${summaryStats.uniqueStudies} Studies`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`${summaryStats.totalRecords} Total Records`}
              size="small"
              color="info"
              variant="outlined"
            />
            <Chip
              label={`Avg +${summaryStats.avgImprovement}% Improvement`}
              size="small"
              color="success"
              variant="outlined"
            />
          </Stack>
        )}

        <Box sx={{ height: 350, minHeight: 300 }}>
          {chartData.labels.length > 0 ? (
            <Bar data={chartData} options={options} />
          ) : (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary'
            }}>
              <Typography>Select at least one study and time period to view data</Typography>
            </Box>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Base Efficiency decreases over time due to oil fouling (ASHRAE degradation).
          Post Unit Efficiency shows sustained performance with PermaFrost NMR treatment.
        </Typography>
      </Box>

      {/* Filter Panel - Right Side */}
      <Paper
        elevation={0}
        sx={{
          width: 130,
          flexShrink: 0,
          p: 1.5,
          bgcolor: (theme) => theme.palette.mode === 'light'
            ? 'rgba(255,255,255,0.9)'
            : 'rgba(0,0,0,0.3)',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          alignSelf: 'flex-start',
          backdropFilter: 'blur(8px)'
        }}
      >
        {/* Time Period Filter */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Years Since Treatment
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={selectedPeriods.size === availablePeriods.length}
                indeterminate={selectedPeriods.size > 0 && selectedPeriods.size < availablePeriods.length}
                onChange={toggleAllPeriods}
              />
            }
            label={<Typography variant="body2">(All)</Typography>}
          />
          {availablePeriods.map(period => (
            <FormControlLabel
              key={period}
              control={
                <Checkbox
                  size="small"
                  checked={selectedPeriods.has(period)}
                  onChange={() => togglePeriod(period)}
                />
              }
              label={<Typography variant="body2">{period}</Typography>}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Study Filter */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Study For
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={selectedStudies.size === longevityData.studies.length}
                indeterminate={selectedStudies.size > 0 && selectedStudies.size < longevityData.studies.length}
                onChange={toggleAllStudies}
              />
            }
            label={<Typography variant="body2">(All)</Typography>}
          />
          {longevityData.studies.map(study => (
            <FormControlLabel
              key={study.id}
              control={
                <Checkbox
                  size="small"
                  checked={selectedStudies.has(study.id)}
                  onChange={() => toggleStudy(study.id)}
                />
              }
              label={<Typography variant="body2">{study.shortName}</Typography>}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Legend */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Measure Names
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#2196F3', mr: 1, borderRadius: 0.5 }} />
          <Typography variant="body2">Avg. Base Eff</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#FF9800', mr: 1, borderRadius: 0.5 }} />
          <Typography variant="body2">Avg. Post Unit Eff</Typography>
        </Box>
      </Paper>
    </Box>
  )
}

export default LongevityPerformance
